import React from "react";
import api from "../lib/api";

export const Spinner = ({ full }) => (
  <div className={`flex items-center justify-center ${full ? "min-h-[60vh]" : "py-16"}`}>
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export const Pill = ({ children, tone = "muted" }) => {
  const tones = {
    muted: "bg-zinc-100 text-zinc-700 border-zinc-300",
    blue: "bg-blue-50 text-primary border-blue-200",
    red: "bg-red-50 text-destructive border-red-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono uppercase tracking-wide border ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const StatBox = ({ value, label, testid }) => (
  <div className="border border-zinc-300 bg-white p-6" data-testid={testid}>
    <div className="font-heading font-900 text-3xl lg:text-4xl tracking-tight tabular-nums">{value}</div>
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);

// Vibrant page themes — blue, green, purple, orange. Backgrounds are deep enough
// for white text (WCAG AA) while staying colorful instead of near-black.
export const VIBRANT_THEMES = [
  { key: "blue", bg: "#1D4ED8", accent: "#BFDBFE" },
  { key: "green", bg: "#047857", accent: "#A7F3D0" },
  { key: "purple", bg: "#6D28D9", accent: "#DDD6FE" },
  { key: "orange", bg: "#C2410C", accent: "#FED7AA" },
];
export const vibrantTheme = (seed = "") => {
  let hash = 0;
  for (const char of String(seed)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return VIBRANT_THEMES[hash % VIBRANT_THEMES.length];
};

// Clickable category icons baked into the sub-page banner artwork (percentage-mapped).
const BANNER_HOTSPOTS = [
  { key: "air", label: "Air Travel", to: "/air", left: "4.5%", width: "6.5%" },
  { key: "rail", label: "Rail Travel", to: "/rail", left: "11.2%", width: "6.5%" },
  { key: "bus", label: "Bus & Coach", to: "/bus", left: "18.0%", width: "6.5%" },
  { key: "ground", label: "Ground Transport", to: "/ground", left: "25.5%", width: "7%" },
  { key: "rideshare", label: "Rideshare", to: "/rideshare", left: "34.5%", width: "8.5%" },
  { key: "road", label: "Road Travel", to: "/road", left: "44.5%", width: "6.5%" },
  { key: "water", label: "Water Travel", to: "/water", left: "52.0%", width: "6.8%" },
  { key: "stay", label: "Stay", to: "/stays", left: "61.5%", width: "6%" },
];

// Legacy AviationBanner — retired in the Feb 2026 minimal redesign. Kept as a
// no-op so any direct imports (e.g. AirlineDetail) don't need code churn.
export const AviationBanner = () => null;

/* Minimal editorial PageHeader — thin band, serif title, no photo, no loud
   color block. Used site-wide as the unified page hero. Extra props
   (`entityCode`, `plain`, `unsplashQuery`) are accepted for backwards-compat
   with older callers but intentionally ignored. */
export const PageHeader = ({ eyebrow, title, subtitle, children }) => (
  <header className="border-b border-border bg-background" data-testid="page-header">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-8 pb-6 sm:pt-10 sm:pb-8">
      {eyebrow && (
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-primary/70 mb-2" data-testid="page-header-eyebrow">
          {eyebrow}
        </div>
      )}
      <h1 className="font-serif font-800 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-primary leading-tight"
        data-testid="page-header-title">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-3 text-base text-muted-foreground max-w-2xl leading-relaxed">{subtitle}</p>
      )}
      {children}
    </div>
  </header>
);

// Renders an Unsplash photo as the PageHeader's dimmed backdrop, with the
// photographer + Unsplash attribution required by their API guidelines.
// Silent no-op when the API is unavailable so the theme color still shows.
const UnsplashBackdrop = ({ query }) => {
  const [photo, setPhoto] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/unsplash/photos", { params: { q: query } });
        const p = (data?.photos || [])[0];
        if (!cancelled) setPhoto(p || null);
        if (p?.download_location) {
          api.post("/unsplash/download", { download_location: p.download_location }).catch(() => {});
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [query]);
  if (!photo) return null;
  return (
    <>
      <img src={photo.image} alt="" aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" aria-hidden="true" />
      <div className="absolute bottom-1.5 right-2 text-[10px] text-white/90 bg-black/40 backdrop-blur-sm px-2 py-0.5 print:hidden"
        data-testid="page-header-unsplash-attribution">
        Photo by <a href={photo.photographer_url} target="_blank" rel="noopener noreferrer" className="underline"
          data-testid="page-header-unsplash-photographer">{photo.photographer_name}</a> on{" "}
        <a href={photo.photo_url} target="_blank" rel="noopener noreferrer" className="underline"
          data-testid="page-header-unsplash-source">Unsplash</a>
      </div>
    </>
  );
};

export const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());

export const TabBar = ({ tabs, active, onChange, attached = false }) => (
  <div className={attached ? "border-b border-zinc-300 bg-white overflow-x-auto" : "max-w-7xl mx-auto px-5 lg:px-8 mt-10 border-b border-zinc-300 overflow-x-auto"}
    data-testid={attached ? "entity-tabs-container" : "entity-tab-bar"}>
    <div className={attached ? "max-w-7xl mx-auto px-5 lg:px-8" : ""}>
      <div className="flex gap-1 min-w-max">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => onChange(t.key)} data-testid={`entity-tab-${t.key}`} aria-selected={active === t.key}
            className={`flex items-center gap-2 min-h-11 px-4 sm:px-5 py-3 text-sm font-700 -mb-px border-b-2 whitespace-nowrap transition-colors ${active === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

import { Link as RouterLink } from "react-router-dom";
import { BRAND, codeGradient } from "../lib/assets";

export const IataBadge = ({ code, name, logo, className = "h-24", size = "text-3xl" }) => {
  const label = code || "—";
  const [g1, g2] = codeGradient(label);
  return (
    <div
      className={`relative w-full ${className} flex flex-col items-center justify-center text-center px-4 overflow-hidden`}
      style={{ backgroundImage: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
      data-testid={`iata-badge-${label}`}
    >
      {name && (
        <span className="font-heading font-900 text-white leading-tight text-lg sm:text-xl line-clamp-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">
          {name}
        </span>
      )}
      <span className={`font-mono font-700 text-white/95 tracking-wide ${name ? "text-base mt-1" : size + " font-900"} drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)]`}>
        {label}
      </span>
      {logo && (
        <img
          src={logo}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute right-2.5 top-2.5 w-9 h-9 object-contain bg-white/95 rounded-md p-1 shadow-sm"
        />
      )}
    </div>
  );
};
